import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";

const TableActionDropdown = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<CSSProperties>({ visibility: "hidden" });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const positionMenu = useCallback(() => {
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button || !menu) return;

    const buttonRect = button.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const gap = 4;
    const edge = 8;
    const top = buttonRect.bottom + gap + menuRect.height <= window.innerHeight
      ? buttonRect.bottom + gap
      : Math.max(edge, buttonRect.top - menuRect.height - gap);
    const left = Math.min(
      Math.max(edge, buttonRect.right - menuRect.width),
      window.innerWidth - menuRect.width - edge,
    );

    setPosition({ left, position: "fixed", top, visibility: "visible", zIndex: 1080 });
  }, []);

  useLayoutEffect(() => {
    if (open) positionMenu();
  }, [open, positionMenu]);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (!buttonRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, positionMenu]);

  return (
    <div className="dropdown ms-2">
      <button
        ref={buttonRef}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Open row actions"
        className="btn btn-icon dropdown-toggle"
        type="button"
        onClick={() => setOpen((current) => !current)}
      >
        <i className="ti ti-dots-vertical" />
      </button>
      {open && createPortal(
        <ul ref={menuRef} className="dropdown-menu show" role="menu" style={position} onClick={() => setOpen(false)}>
          {children}
        </ul>,
        document.body,
      )}
    </div>
  );
};

export default TableActionDropdown;
